import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { IconButton, Typography, Tooltip, Pagination, Box, CircularProgress } from "@mui/material";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import Cookies from "universal-cookie";
import {
  deletewithQueryApiServices,
  getWithQueryApiServices,
  putWithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { ListBox, StyledTypography } from "../../../styles/commonStyles";
import { IconColor } from "../../../styles/constants";
import { successToast } from "../../../utils/helper";
import { CommonSearch } from "../../shared/CommonSearch";

import { useUserRole } from "../../../hooks/useUserRole";

export const CompanyUsers = ({ companyForm, addNewCompany }) => {
  const cookies = new Cookies();
  const defaultcompany = cookies.get("defaultcompany");
  const [searchUser, setSearchUser] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [userPaginationModel, setUserPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  });
  const [accumulatedUsers, setAccumulatedUsers] = React.useState([]);
  const { tenantString } = useOutletContext();
  const queryClient = useQueryClient();
  
  // Role verification using the selected company ID
  const { isCurrentUserSysAdmin } = useUserRole(companyForm?.companyID);

  // Determine if pane should be active/unblurred based on role explicitly for THIS company
  const isPaneActive = addNewCompany && isCurrentUserSysAdmin;

  const handleSortToggle = () => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    console.log("Sort order changed to:", newSortOrder);
    setSortOrder(newSortOrder);
  };

  const { data: usersData, refetch: refetchUserList } = useQuery({
    queryKey: ["usersData", companyForm, searchUser, tenantString, sortOrder],
    queryFn: () => {
      const queryString = `pageNumber=1&pageSize=10000&selectedCompany=${companyForm?.companyID}&search=${searchUser}&sortOrder=${sortOrder}${tenantString}`;
      console.log("usersData API Query:", queryString);
      return getWithQueryApiServices(apiRoutes.GET_USERS_LIST, queryString);
    },
    enabled: !!companyForm?.companyID,
  });

  const { data: allUsersList, isFetching: isAllUsersFetching } = useQuery({
    queryKey: ["allUsersList", searchUser, sortOrder, tenantString, userPaginationModel],
    queryFn: () => {
      const queryString = `pageNumber=${userPaginationModel.page + 1}&pageSize=${userPaginationModel.pageSize}&search=${searchUser}&sortOrder=${sortOrder}${tenantString}`;
      console.log("allUsersList API Query:", queryString);
      return getWithQueryApiServices(apiRoutes.GET_ALL_USERS_LIST, queryString);
    },
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

  const lastUserScrollRef = React.useRef(0);
  const handleScroll = (e) => {
    const now = Date.now();
    if (now - lastUserScrollRef.current < 100) return;
    lastUserScrollRef.current = now;

    if (isAllUsersFetching) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Trigger load 150px before hitting the bottom for a seamless load experience
    if (scrollHeight - scrollTop - clientHeight < 150) {
      if (allUsersList?.data?.count !== undefined && accumulatedUsers.length >= allUsersList?.data?.count) {
        return;
      }
      setUserPaginationModel((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  };

  React.useEffect(() => {
    setUserPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchUser, sortOrder, companyForm?.companyID]);

  React.useEffect(() => {
    if (allUsersList?.data?.userList) {
      if (userPaginationModel.page === 0) {
        setAccumulatedUsers(allUsersList.data.userList);
      } else {
        setAccumulatedUsers((prev) => {
          const existingIds = new Set(prev.map((u) => u.userID));
          const newUsers = allUsersList.data.userList.filter((u) => !existingIds.has(u.userID));
          return [...prev, ...newUsers];
        });
      }
    }
  }, [allUsersList, userPaginationModel.page]);


  const addUserForCompany = useMutation({
    mutationKey: ["addUserForCompany"],
    mutationFn: (workData) =>
      putWithQueryApiServices(
        apiRoutes.ADD_USER_TO_COMPANY,
        `selectedCompany=${companyForm?.companyID}${tenantString}`,
        workData
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usersData"] });
      refetchUserList();
      successToast("User Added Successfully");
    },
  });

  const removeUser = useMutation({
    mutationKey: ["removeUser"],
    mutationFn: (id) =>
      deletewithQueryApiServices(
        apiRoutes.DELETE_COMPANY_USER,
        `selectedCompany=${defaultcompany}${tenantString}`,
        id
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usersData"] });
      refetchUserList();
      successToast("User removed from company");
    },
  });

  const { data: allUserAccess } = useQuery({
    queryKey: ["allUserAccess", tenantString],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_ALL_ACCESS_OF_USER,
        `selectedCompany=1${tenantString}`
      ),
  });

  const checkUserExist = (data) => {
    const getCompany = usersData?.data?.userList?.find(
      (item) => item?.userID === data?.userID
    );
    return getCompany?.levelList?.length ? true : false;
  };

  const addUsertoCompany = (data) => {
    let workData = { ...data };
    // ✅ Grant only base "Search" permission by default so they show as "Added", but not as Admin/Editor
    if (!workData?.levelList?.length) {
      const basePermissions = ['search'];
      workData.levelList = allUserAccess?.data?.filter(p => basePermissions.includes(p.toLowerCase())) || [];
      // Fallback: if 'Search' is not found, just take the first one available
      if (workData.levelList.length === 0 && allUserAccess?.data?.length > 0) {
        workData.levelList = [allUserAccess.data[0]];
      }
    }

    if (companyForm?.companyID) {
      addUserForCompany.mutate(workData);
    }
  };

  const removeUserForCompany = (data) => {
    if (companyForm?.companyID) {
      removeUser.mutate(`${companyForm?.companyID}/${data?.userID}`);
    }
  };

  return (
    <div
      id="tour-company-users-pane"
      style={{
        height: "100%",
        padding: "4px 0 0 0",
        boxSizing: "border-box",
        borderRadius: "6px",
        pointerEvents: !isPaneActive ? "none" : "auto",
        backgroundColor: !isPaneActive ? "#f0f0f0" : "white",
        opacity: !isPaneActive ? "0.6" : "1",
        filter: !isPaneActive ? "blur(5px)" : "none",
        transition: "filter 0.3s ease",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ padding: "0 20px" }}>
        <StyledTypography
          title={!addNewCompany ? "Add Users to Company" : !isCurrentUserSysAdmin ? "Access Denied" : `Add Users to Company ${companyForm?.name}`}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {!addNewCompany 
            ? "Add Users to Company"
            : !isCurrentUserSysAdmin 
              ? "Access Denied" 
              : `Add Users to Company ${companyForm?.name}`}
        </StyledTypography>
        
        {/* Search and Sort Container */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 10,
          }}
        >
          <CommonSearch
            style={{ flex: 1 }}
            label={"All Users"}
            value={searchUser}
            handleChange={(e) => {
              setSearchUser(e.target.value);
            }}
          />
          
          {/* Sort Button */}
          <Tooltip
            title={sortOrder === "asc" ? "Sort Z-A" : "Sort A-Z"}
            placement="top"
          >
            <span>
              <IconButton
                onClick={handleSortToggle}
                disabled={!isPaneActive}
                style={{
                  backgroundColor: !isPaneActive ? "#f5f5f5" : "#e3f2fd",
                  borderRadius: 8,
                  padding: 8,
                  minWidth: 44,
                }}
              >
                <SortByAlphaIcon
                  style={{
                    color: !isPaneActive ? "#ccc" : IconColor,
                    fontSize: 22,
                  }}
                />
                {sortOrder === "asc" ? (
                  <ArrowUpwardIcon
                    style={{
                      color: !isPaneActive ? "#ccc" : IconColor,

                      fontSize: 16,
                      marginLeft: -4,
                    }}
                  />
                ) : (
                  <ArrowDownwardIcon
                    style={{
                      color: !isPaneActive ? "#ccc" : IconColor,
                      fontSize: 16,
                      marginLeft: -4,
                    }}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
      
      <div
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: "auto",
          marginTop: 20,
          paddingRight: "8px", // Space for scrollbar
          position: "relative",
        }}
      >
        {isAllUsersFetching && userPaginationModel.page === 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              backdropFilter: "blur(1px)",
              zIndex: 2,
            }}
          >
            <CircularProgress size={30} sx={{ color: "#7285b3" }} />
          </Box>
        )}
        {addNewCompany && !isCurrentUserSysAdmin ? (
          <StyledTypography style={{ textAlign: "center", color: "#d32f2f", marginTop: 20 }}>
            You do not have permission to view or map users for this company.
          </StyledTypography>
        ) : companyForm?.companyID || addNewCompany ? (
          <>
            {accumulatedUsers?.map((user, i) => (
              <ListBox
                key={i}
                style={{
                  padding: "6px 12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: checkUserExist(user) ? "#deedf5" : "white",
                  borderRadius: 6,
                  cursor: "default",
                }}
              >
                 <IconButton
                  disabled={!isPaneActive || checkUserExist(user) || addUserForCompany.isPending}
                  onClick={() => addUsertoCompany(user)}
                  style={{
                    padding: 8,
                  }}
                >
                  {addUserForCompany.isPending && addUserForCompany.variables?.userID === user.userID ? (
                    <CircularProgress size={24} sx={{ color: IconColor }} />
                  ) : (
                    <GroupAddIcon
                      style={{
                        color: (!isPaneActive || checkUserExist(user)) ? "#ccc" : IconColor,
                        fontSize: 28,
                      }}
                    />
                  )}
                </IconButton>

                <Typography style={{ flexGrow: 1, paddingLeft: 16, paddingRight: 16 }}>
                  {user?.username}
                  {user?.fullName ? ` (${user.fullName})` : ""}
                </Typography>

                <IconButton
                  disabled={!isPaneActive || !checkUserExist(user) || removeUser.isPending}
                  onClick={() => removeUserForCompany(user)}
                  style={{
                    padding: 8,
                  }}
                >
                  {removeUser.isPending && removeUser.variables?.endsWith("/" + user.userID) ? (
                    <CircularProgress size={24} sx={{ color: IconColor }} />
                  ) : (
                    <PersonRemoveIcon
                      style={{
                        color: (!isPaneActive || !checkUserExist(user)) ? "#ccc" : IconColor,
                        fontSize: 28,
                      }}
                    />
                  )}
                </IconButton>
              </ListBox>
            ))}
            {isAllUsersFetching && userPaginationModel.page > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} sx={{ color: "#7285b3" }} />
              </Box>
            )}
          </>
        ) : (
          <StyledTypography style={{ textAlign: "center" }}>
            Select any company
          </StyledTypography>
        )}
      </div>


    </div>
  );
};